import { useChatApi } from '@/hooks/useChatApi';
import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { AvailableLanguage, BotProductCardVelioMessage, ChatMessage } from '@/types';
import { ChatWidgetEvents, dispatchEventToWindow } from '@/utils/events';
import { motion } from 'framer-motion';
import { memo, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

type MessageProductCardProps = {
  message: BotProductCardVelioMessage;
  messageId: string;
};

const ShowMoreIcon = () => {
  return (
    <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_5452_108217)">
        <rect width="22" height="20" fill="white" />
        <g clip-path="url(#clip1_5452_108217)">
          <path
            d="M20.5 18.5L17.5 15.5M18.5 13C18.5 14.933 16.933 16.5 15 16.5C13.067 16.5 11.5 14.933 11.5 13C11.5 11.067 13.067 9.5 15 9.5C16.933 9.5 18.5 11.067 18.5 13Z"
            stroke="#0D0D0D"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
        <path d="M1 6V3C1 1.89543 1.89543 1 3 1H6" stroke="#0D0D0D" stroke-width="1.5" />
        <path d="M21 6V3C21 1.89543 20.1046 1 19 1H16" stroke="#0D0D0D" stroke-width="1.5" />
        <path d="M1 13L1 16C1 17.1046 1.89543 18 3 18L6 18" stroke="#0D0D0D" stroke-width="1.5" />
      </g>
      <defs>
        <clipPath id="clip0_5452_108217">
          <rect width="22" height="20" fill="white" />
        </clipPath>
        <clipPath id="clip1_5452_108217">
          <rect width="12" height="12" fill="white" transform="translate(10 8)" />
        </clipPath>
      </defs>
    </svg>
  );
};

const InfoIcon = () => {
  return (
    <svg
      className="ml-[8px]"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.75 12.96H9V9.95996H8.25M9 6.95996H9.0075M15.75 9.95996C15.75 13.6879 12.7279 16.71 9 16.71C5.27208 16.71 2.25 13.6879 2.25 9.95996C2.25 6.23204 5.27208 3.20996 9 3.20996C12.7279 3.20996 15.75 6.23204 15.75 9.95996Z"
        stroke="#D9D9D9"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

const mapLangToShowMoreText = (language: AvailableLanguage) => {
  switch (language) {
    case 'en':
      return 'Show more like this';
    case 'de':
      return 'Mehr wie dieses anzeigen';
    case 'fr':
      return 'Afficher plus comme ceci';
    case 'nl':
      return 'Toon meer als dit';
  }
};

const MessageProductCardVelio = ({ message, messageId }: MessageProductCardProps) => {
  const { config } = useConfigStore();

  const { chatSession, setOpen } = useChatStore();

  const { sendMessageMutation } = useChatApi();
  const language = config.language;

  // Memoize showMoreText to prevent recalculation
  const showMoreText = useMemo(
    () => mapLangToShowMoreText(language as AvailableLanguage),
    [language]
  );

  // Memoize product link to prevent recalculation
  const productLink = useMemo(() => {
    const link = message.content.link;
    if (!link || link === '#') {
      return '#';
    }

    try {
      // Try to parse as absolute URL
      if (link.startsWith('http://') || link.startsWith('https://')) {
        const url = new URL(link);
        return url.pathname;
      }
      // If it's a relative path, return it as is
      if (link.startsWith('/')) {
        return link;
      }
      // Try to parse with a dummy base URL
      const url = new URL(link, 'https://example.com');
      return url.pathname;
    } catch {
      // If URL parsing fails, return the original link or '/'
      return link.startsWith('/') ? link : '/';
    }
  }, [message.content.link]);

  const handleShowMore = useCallback(() => {
    const userMessageId = uuidv4();

    const threadId = chatSession.threadId;
    if (!threadId) {
      throw new Error('Thread ID is not set');
    }

    const userMessage: ChatMessage = {
      id: userMessageId,
      output: [
        {
          role: 'user',
          type: 'show-more',
          content: {
            image: message?.content?.image,
            brandName: message?.content?.brandName,
            productName: message?.content?.name,
          },
        },
      ],
      messageId: userMessageId,
      threadId: threadId,
      timestamp: new Date().toISOString(),
    };

    useChatStore.getState().addMessage(userMessage);

    sendMessageMutation.mutateAsync({
      content: `Show more like this ${message.content.productId}`,
      options: {
        addMessageToChatHistory: false,
      },
    });
  }, [message, sendMessageMutation, chatSession.threadId]);

  // Memoize onClick handler for product card
  const handleCardClick = useCallback(() => {
    dispatchEventToWindow(ChatWidgetEvents.ON_NAVIGATE, {
      link: productLink,
    });
    setOpen(false);
  }, [productLink, setOpen]);

  // Memoize onClick handler for show more button
  const handleShowMoreClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      handleShowMore();
    },
    [handleShowMore]
  );

  return (
    <motion.div
      className="flex max-w-full cursor-pointer flex-col"
      layoutId={`${messageId}-product-card`}
      key={`${messageId}`}
    >
      <div className="flex max-w-full cursor-pointer flex-col" onClick={handleCardClick}>
        <img
          src={message.content.image}
          alt={message.content.name}
          className="aspect-[252/168] max-h-[300px] max-w-full rounded-md object-cover"
        />
        <div className="mt-[8px] flex flex-col">
          <div className="flex items-center justify-between gap-[8px]">
            <img
              src={message.content.brandLogo}
              alt={message.content.name}
              className="h-auto max-h-[60px] max-w-[174px] rounded-full object-cover"
            />
            <div className="flex items-center">
              {message.content.rating && (
                <span className="mr-[3px] text-sm font-semibold text-widget-text-primary">
                  {message.content.rating}
                </span>
              )}
              {message.content.condition && (
                <span className="text-sm text-[#8C8C8C]">{message.content.condition}</span>
              )}
              <InfoIcon />
            </div>
          </div>
          <p className="text-text-primary text-md font-semibold">{message.content.name}</p>
          <div className="mt-[12px] flex items-center gap-[12px]">
            <span className="text-text-tertiary text-md font-semibold">
              {message.content.actualPrice}
            </span>
            <span className="text-md text-[#8C8C8C] line-through">{message.content.oldPrice}</span>
            {message.content.saleTag && (
              <span className="flex h-[20px] translate-y-[1px] items-center rounded-[20px] bg-[#EF4444] px-[8px] py-[2px] text-[12px] font-bold leading-[12px] tracking-[0.04em] text-widget-white">
                {message.content.saleTag}
              </span>
            )}
          </div>
          {message.content.tags && (
            <div className="mt-[12px] flex flex-wrap items-center gap-[4px]">
              {message.content.tags.map((tag, index) => (
                <>
                  <span key={tag} className="text-sm font-medium text-[#8C8C8C]">
                    {tag}
                  </span>
                  {index !== message.content.tags.length - 1 && (
                    <span className="h-[4px] w-[4px] translate-y-[2px] rounded-full bg-[#8C8C8C]" />
                  )}
                </>
              ))}
            </div>
          )}
        </div>
        <button className="mt-[16px] flex items-center gap-[12px]" onClick={handleShowMoreClick}>
          <ShowMoreIcon />
          <span className="text-sm font-medium text-[#0D0D0D]">{showMoreText}</span>
        </button>
      </div>
    </motion.div>
  );
};

export default memo(MessageProductCardVelio, (prevProps, nextProps) => {
  // Only re-render if messageId or message content changes
  if (prevProps.messageId !== nextProps.messageId) {
    return false;
  }

  // Deep comparison of message content
  const prevContent = prevProps.message.content;
  const nextContent = nextProps.message.content;

  return (
    prevContent.productId === nextContent.productId &&
    prevContent.name === nextContent.name &&
    prevContent.image === nextContent.image &&
    prevContent.link === nextContent.link &&
    prevContent.brandName === nextContent.brandName &&
    prevContent.brandLogo === nextContent.brandLogo &&
    prevContent.actualPrice === nextContent.actualPrice &&
    prevContent.oldPrice === nextContent.oldPrice &&
    prevContent.rating === nextContent.rating &&
    prevContent.condition === nextContent.condition &&
    prevContent.saleTag === nextContent.saleTag &&
    JSON.stringify(prevContent.tags) === JSON.stringify(nextContent.tags)
  );
});
